import { useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import {
  useWbsProgramLabelSearchLazyQuery,
  WbsLabelValueSearchResult,
  WbsProgramLabelSearchQueryResult,
  WbsLabelValueSearchInput,
  WbsProgramLabelSearchQuery,
  WbsProgramLabelSearchDocument
} from 'graphql/types';
import { FacetOption } from 'modules/notes/common';
import { SearchHandlerParams } from '../../LabelFacetDetails';
export const PROGRAMS_PAGE_COUNT = 50;

const getProgramOptions = (
  data: WbsProgramLabelSearchQuery | undefined
): WbsLabelValueSearchResult[] =>
  data?.wbsLabelValueSearch?.programs.items.map((item) => ({
    ...item,
    id: item.id ?? null
  })) ?? [];

export type ProgramLabelSearchResult = {
  options: WbsLabelValueSearchResult[];
  loading: boolean;
  searchHandler: (params: SearchHandlerParams) => void;
  hasMore: boolean;
};
export const useProgramLabelSearch = (): ProgramLabelSearchResult => {
  const [getPrograms, { data, loading }] = useWbsProgramLabelSearchLazyQuery();

  const searchHandler = useCallback(
    ({ query }: SearchHandlerParams) => {
      const variables = {
        date: DateTime.local().toISODate(),
        input: {
          count: PROGRAMS_PAGE_COUNT,
          query
        }
      };
      getPrograms({ variables });
    },
    [getPrograms]
  );
  const programs = useMemo(() => getProgramOptions(data), [data]);
  return useMemo(
    () => ({
      searchHandler,
      loading,
      options: programs,
      hasMore: programs.length === PROGRAMS_PAGE_COUNT
    }),
    [loading, searchHandler, programs]
  );
};

export const searchPrograms = ({
  client: apolloClient
}: {
  client: any;
}) => async (
  input: WbsLabelValueSearchInput
): Promise<WbsLabelValueSearchResult[]> => {
  const { data }: WbsProgramLabelSearchQueryResult = await apolloClient.query({
    query: WbsProgramLabelSearchDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: input
    }
  });

  return getProgramOptions(data);
};

const transformProgram = (
  data: WbsProgramLabelSearchQuery | undefined
): FacetOption[] =>
  data?.wbsLabelValueSearch?.programs.items.map((program) => ({
    ...program,
    label: program.displayText,
    key: program.displayText,
    id: program.id || undefined
  })) ?? [];

export const searchProgramsByTerm = ({
  client: apolloClient
}: {
  client: any;
}) => async (searchTerm: string): Promise<FacetOption[]> => {
  const { data }: WbsProgramLabelSearchQueryResult = await apolloClient.query({
    query: WbsProgramLabelSearchDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: { count: 10, query: searchTerm }
    }
  });

  return transformProgram(data);
};

export default useProgramLabelSearch;
